/**
 * Secure Key Storage using IndexedDB
 * Provides better protection against XSS compared to localStorage
 * Keys are stored encrypted using a session-derived key
 */

import { arrayBufferToBase64, base64ToArrayBuffer } from '@/lib/crypto-utils';
import { getPublicOrigin } from '@/lib/public-url';

const DB_NAME = 'elefa_secure_storage';
const DB_VERSION = 1;
const STORE_NAME = 'encryption_keys';

interface StoredKeyPair {
  id: string;
  publicKey: string;
  encryptedPrivateKey: string;
  iv: string;
  salt: string;
  createdAt: number;
}

// Open or create the IndexedDB database
function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(new Error('Failed to open secure storage'));
    
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

// Derive a wrapping key from stable, origin-specific data
// IMPORTANT: Do NOT use navigator.userAgent - it changes with browser updates
// and across devices, making stored private keys permanently inaccessible.
// Also do NOT use the raw page origin - on Capacitor it resolves to
// `capacitor://localhost`, yielding a different wrapping key on native vs.
// web for the same user account. getPublicOrigin() normalises both.
async function deriveWrappingKey(salt: ArrayBuffer): Promise<CryptoKey> {
  const sessionData = new TextEncoder().encode(
    `${getPublicOrigin()}-elefa-e2ee-v2`
  );
  
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    sessionData,
    'PBKDF2',
    false,
    ['deriveKey']
  );
  
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: new Uint8Array(salt),
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

// Encrypt the private key before storage
async function encryptPrivateKey(
  privateKey: string,
  salt: ArrayBuffer,
  iv: ArrayBuffer
): Promise<ArrayBuffer> {
  const wrappingKey = await deriveWrappingKey(salt);
  const encoded = new TextEncoder().encode(privateKey);
  
  return crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: new Uint8Array(iv) },
    wrappingKey,
    encoded
  );
}

// Decrypt the private key from storage
async function decryptPrivateKey(
  encryptedData: ArrayBuffer,
  salt: ArrayBuffer,
  iv: ArrayBuffer
): Promise<string> {
  const wrappingKey = await deriveWrappingKey(salt);
  
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: new Uint8Array(iv) },
    wrappingKey,
    encryptedData
  );
  
  return new TextDecoder().decode(decrypted);
}

// arrayBufferToBase64 and base64ToArrayBuffer imported from @/lib/crypto-utils

/**
 * Store encryption key pair securely in IndexedDB
 * Private key is encrypted before storage
 */
export async function storeKeyPairSecure(
  publicKey: string,
  privateKey: string
): Promise<void> {
  const db = await openDatabase();
  
  // Generate random salt and IV for this key pair
  const saltArray = crypto.getRandomValues(new Uint8Array(16));
  const ivArray = crypto.getRandomValues(new Uint8Array(12));
  const salt = saltArray.buffer as ArrayBuffer;
  const iv = ivArray.buffer as ArrayBuffer;
  
  // Encrypt the private key
  const encryptedPrivateKey = await encryptPrivateKey(privateKey, salt, iv);
  
  const storedData: StoredKeyPair = {
    id: 'user_keypair',
    publicKey,
    encryptedPrivateKey: arrayBufferToBase64(encryptedPrivateKey),
    iv: arrayBufferToBase64(iv),
    salt: arrayBufferToBase64(salt),
    createdAt: Date.now(),
  };
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(storedData);
    
    request.onerror = () => reject(new Error('Failed to store keys'));
    request.onsuccess = () => resolve();
    
    transaction.oncomplete = () => db.close();
  });
}

/**
 * Retrieve the stored public key
 */
export async function getStoredPublicKeySecure(): Promise<string | null> {
  try {
    const db = await openDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get('user_keypair');
      
      request.onerror = () => reject(new Error('Failed to retrieve keys'));
      request.onsuccess = () => {
        const data = request.result as StoredKeyPair | undefined;
        resolve(data?.publicKey || null);
      };
      
      transaction.oncomplete = () => db.close();
    });
  } catch {
    return null;
  }
}

/**
 * Retrieve and decrypt the stored private key
 */
export async function getStoredPrivateKeySecure(): Promise<string | null> {
  try {
    const db = await openDatabase();
    
    const data = await new Promise<StoredKeyPair | undefined>((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get('user_keypair');
      
      request.onerror = () => reject(new Error('Failed to retrieve keys'));
      request.onsuccess = () => resolve(request.result as StoredKeyPair | undefined);
      
      transaction.oncomplete = () => db.close();
    });
    
    if (!data) return null;
    
    const encryptedData = base64ToArrayBuffer(data.encryptedPrivateKey);
    const iv = base64ToArrayBuffer(data.iv);
    const salt = base64ToArrayBuffer(data.salt);
    
    try {
      return await decryptPrivateKey(encryptedData, salt, iv);
    } catch {
      // Wrapping key derivation changed (e.g. browser update removed userAgent from seed)
      // Clear the stale entry so initializeEncryption regenerates keys
      console.warn('Private key undecryptable (wrapping key changed), clearing stale entry');
      await clearStoredKeysSecure();
      return null;
    }
  } catch (error) {
    console.error('Failed to retrieve private key:', error);
    return null;
  }
}

/**
 * Check if keys are stored
 */
export async function hasStoredKeysSecure(): Promise<boolean> {
  try {
    // Must verify the private key is actually decryptable, not just that public key exists
    const privateKey = await getStoredPrivateKeySecure();
    return privateKey !== null;
  } catch {
    return false;
  }
}

/**
 * Clear all stored keys
 */
export async function clearStoredKeysSecure(): Promise<void> {
  try {
    const db = await openDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete('user_keypair');
      
      request.onerror = () => reject(new Error('Failed to clear keys'));
      request.onsuccess = () => resolve();
      
      transaction.oncomplete = () => db.close();
    });
  } catch {
    // Ignore errors during cleanup
  }
}

/**
 * Migrate keys from localStorage to IndexedDB (one-time migration)
 */
export async function migrateFromLocalStorage(): Promise<boolean> {
  const PRIVATE_KEY_STORAGE = 'elefa_encryption_private_key';
  const PUBLIC_KEY_STORAGE = 'elefa_encryption_public_key';
  
  const privateKey = localStorage.getItem(PRIVATE_KEY_STORAGE);
  const publicKey = localStorage.getItem(PUBLIC_KEY_STORAGE);
  
  if (privateKey && publicKey) {
    try {
      // Check if already migrated
      const hasSecure = await hasStoredKeysSecure();
      if (!hasSecure) {
        await storeKeyPairSecure(publicKey, privateKey);
      }
      
      // Clear localStorage keys after successful migration
      localStorage.removeItem(PRIVATE_KEY_STORAGE);
      localStorage.removeItem(PUBLIC_KEY_STORAGE);
      
      console.log('E2EE keys migrated to secure storage');
      return true;
    } catch (error) {
      console.error('Failed to migrate keys:', error);
      return false;
    }
  }
  
  return false;
}
